import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FirebaseError } from 'firebase/app'
import Lock from 'lucide-react/dist/esm/icons/lock'
import Loader2 from 'lucide-react/dist/esm/icons/loader-2'
import ShieldCheck from 'lucide-react/dist/esm/icons/shield-check'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { useAuth } from '@/contexts/AuthContext'
import { cn } from '@/lib/utils'

function isValidEmail(value: string): boolean {
  const trimmed = value.trim()
  if (!trimmed) return false
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)
}

function getEmailError(value: string): string | null {
  const trimmed = value.trim()
  if (!trimmed) return 'Please enter your email address.'
  if (!isValidEmail(trimmed)) return 'Please enter a valid email address.'
  return null
}

function getPasswordError(value: string): string | null {
  if (!value) return 'Please enter your password.'
  return null
}

function loginInputClassName(hasError: boolean) {
  return cn(
    'h-12 rounded-xl border-border/60 bg-white focus-visible:outline-none focus-visible:ring-1',
    hasError
      ? 'border-destructive focus-visible:border-destructive focus-visible:ring-destructive/40'
      : 'focus-visible:border-primary focus-visible:ring-primary/40'
  )
}

/** Maps Firebase Auth error codes to friendly, non-revealing messages. */
function messageForError(error: unknown): string {
  if (error instanceof FirebaseError) {
    switch (error.code) {
      case 'auth/invalid-email':
        return 'Please enter a valid email address.'
      case 'auth/user-disabled':
        return 'This account has been disabled. Please contact your Mezza at support@mezzapay.com.'
      case 'auth/too-many-requests':
        return 'Too many attempts. Please wait a moment and try again.'
      case 'auth/invalid-credential':
      case 'auth/wrong-password':
      case 'auth/user-not-found':
        return 'Incorrect email or password.'
      case 'auth/network-request-failed':
        return 'Network error. Check your connection and try again.'
    }
  }
  return 'Something went wrong. Please try again.'
}

export function Login() {
  const navigate = useNavigate()
  const { signInWithEmail } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [emailTouched, setEmailTouched] = useState(false)
  const [passwordTouched, setPasswordTouched] = useState(false)

  const emailValidationError = getEmailError(email)
  const passwordValidationError = getPasswordError(password)
  const emailError = emailTouched ? emailValidationError : null
  const passwordError = passwordTouched ? passwordValidationError : null
  const canSubmit = !loading && !emailValidationError && !passwordValidationError

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return

    setLoading(true)
    setErrorMessage(null)

    try {
      await signInWithEmail(email, password)
      navigate('/', { replace: true })
    } catch (error) {
      setErrorMessage(messageForError(error))
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-white px-6 py-12">
      <div className="w-full max-w-md space-y-5">
        <Card className="gap-0 overflow-hidden rounded-3xl border border-border/40 bg-white py-0 shadow-[0_2px_20px_rgba(0,0,0,0.05)]">
          <div className="border-b border-border/30 px-8 pb-5 pt-9 text-center">
            <img
              src="/mezza_wordmark.png"
              alt="Mezza"
              className="mx-auto h-5 w-auto"
              width={101}
              height={18}
            />

            <div className="mt-7 space-y-5">
              <div className="inline-flex items-center gap-2 rounded-full border border-border/50 bg-white px-3.5 py-1.5 text-xs font-semibold text-foreground">
                <ShieldCheck className="h-4 w-4 text-success" aria-hidden="true" />
                Verified portal
              </div>

              <div className="space-y-2.5">
                <h1 className="text-xl font-semibold tracking-tight text-foreground">
                  Document verification
                </h1>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  <span className="sm:hidden">
                    <span className="block">Securely upload partnership</span>
                    <span className="block">documents requested by your Mezza contact</span>
                  </span>
                  <span className="hidden sm:inline">
                    Securely upload the partnership documents
                    <br />
                    requested by your Mezza contact
                  </span>
                </p>
              </div>
            </div>
          </div>

          <CardHeader className="px-8 pb-0 pt-5">
            <CardTitle className="text-lg font-semibold">Sign in</CardTitle>
            <CardDescription>
              Use the credentials provided by your Mezza contact
            </CardDescription>
          </CardHeader>

          <CardContent className="px-8 pb-8 pt-6">
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium">
                  Email address
                </label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    setErrorMessage(null)
                  }}
                  onBlur={() => setEmailTouched(true)}
                  placeholder="name@example.com"
                  aria-invalid={!!emailError}
                  aria-describedby={emailError ? 'email-error' : undefined}
                  className={loginInputClassName(!!emailError)}
                  disabled={loading}
                />
                {emailError && (
                  <p id="email-error" role="alert" className="text-sm text-destructive">
                    {emailError}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium">
                  Password
                </label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value)
                    setErrorMessage(null)
                  }}
                  onBlur={() => setPasswordTouched(true)}
                  placeholder="Enter your password"
                  aria-invalid={!!passwordError}
                  aria-describedby={passwordError ? 'password-error' : undefined}
                  className={loginInputClassName(!!passwordError)}
                  disabled={loading}
                />
                {passwordError && (
                  <p id="password-error" role="alert" className="text-sm text-destructive">
                    {passwordError}
                  </p>
                )}
              </div>

              {errorMessage && (
                <p role="alert" className="text-sm text-destructive">
                  {errorMessage}
                </p>
              )}

              <Button
                type="submit"
                className="h-12 w-full rounded-xl text-base font-bold"
                disabled={!canSubmit}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="rounded-2xl border border-border/60 bg-white p-5 text-center shadow-[0_2px_16px_rgba(0,0,0,0.04)]">
          <div className="flex items-center justify-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-success-bg/60">
              <Lock className="h-5 w-5 text-success-border" aria-hidden="true" />
            </span>
            <span className="text-sm font-semibold text-foreground">Encrypted connection</span>
          </div>
          <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
            Your documents are transmitted over a secure, encrypted connection and are accessible
            only to the Mezza risk team
          </p>
        </div>
      </div>
    </div>
  )
}
